import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Download } from "lucide-react";
import { useForm } from "react-hook-form";
import { products } from "@/lib/products";
import Layout from "@/components/Layout";
import { useToast } from "@/hooks/use-toast";

interface InquiryForm {
  name: string;
  phone: string;
  email: string;
  country: string;
  requirement: string;
}

export default function Products() {
  const { register, handleSubmit, reset } = useForm<InquiryForm>();
  const { toast } = useToast();

  const onSubmit = async (data: InquiryForm) => {
    const formData = {
      access_key: "59e9a68c-6f3f-4cd0-996e-0044fb9e0f56",
      name: data.name,
      phone: data.phone,
      email: data.email,
      country: data.country,
      message: data.requirement,
      subject: "Product Inquiry from Products Page",
    };

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.status === 200) {
        toast({
          title: "Inquiry Submitted Successfully!",
          description: "We will get back to you soon.",
        });
        reset();
      } else {
        throw new Error(result.message || "Something went wrong!");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit the form. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getTruncatedDescription = (description: string) => {
    const firstSentence = description.split('.')[0] + '.';
    return firstSentence;
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-center mb-12">Our Products</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {products.map((product) => (
            <Card key={product.id} className="product-card-hover flex flex-col">
              <CardHeader>
                <img
                  src={product.images[0]}
                  alt={product.name}
                  className="w-full h-48 object-cover rounded-t-lg"
                  loading="lazy"
                />
                <CardTitle className="text-xl mt-4">{product.name}</CardTitle>
              </CardHeader>
              <CardContent className="flex-grow">
                <p className="text-muted-foreground">
                  {getTruncatedDescription(product.description)}
                </p>
              </CardContent>
              <CardFooter className="pt-4">
                <Link to={`/products/${product.id}`} className="w-full">
                  <Button 
                    className="w-full bg-[#ea384c] hover:bg-[#ea384c]/90"
                  >
                    View Details
                  </Button>
                </Link>
              </CardFooter>
            </Card>
          ))}
        </div>

        <div className="flex justify-center mb-16">
          <Button 
            variant="outline" 
            size="lg"
            className="flex items-center gap-2"
            onClick={() => window.open('/catalogue.pdf', '_blank')}
          >
            <Download className="w-5 h-5" />
            Download Product Catalogue
          </Button>
        </div>

        <div className="max-w-4xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>Send Inquiry</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input placeholder="Name" {...register("name")} required />
                  <Input placeholder="Phone Number" {...register("phone")} required />
                  <Input placeholder="Email" type="email" {...register("email")} required />
                  <Input placeholder="Country" {...register("country")} required />
                  <Textarea 
                    placeholder="Requirement" 
                    {...register("requirement")} 
                    className="md:col-span-2"
                    required
                  />
                </div>
                <Button type="submit" className="w-full bg-[#ea384c] hover:bg-[#ea384c]/90">
                  Send Inquiry
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
